import { MontoCRC, MontoUSD } from '@/components/dominio';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCuentasBancoBC } from '@/hooks/useCuentasBancoBC';
import { useArchivoBancoBNPropuesta } from '@/hooks/usePropuestasPago';
import { esCuentaBNCR } from '@adelante/shared';
import type { ArchivoBancoBNResultado, MotivoExclusionBN } from '@adelante/shared';
import { DownloadSimple, Warning } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

interface Props {
  idPropuesta: number;
  /** Cuentas bancarias de origen ya usadas en la propuesta. */
  cuentasUsadas: string[];
  open: boolean;
  onClose: () => void;
}

const TEXTO_MOTIVO: Record<MotivoExclusionBN, string> = {
  'sin-proveedor': 'No está en Proveedores',
  'sin-iban': 'Sin IBAN cargado',
  'iban-formato': 'IBAN con formato inválido (debe ser CR + 20 dígitos)',
  'sin-cedula': 'Sin cédula válida (9 dígitos física, 10 jurídica, 11-12 extranjero)',
  'cedula-incompleta':
    'Cédula de 9 dígitos con formato de jurídica (3-101…): parece faltar un dígito',
};

/** Fecha local (no UTC: de noche en Costa Rica `toISOString` ya cae en el día siguiente). */
function hoyISO(): string {
  const ahora = new Date();
  const mes = String(ahora.getMonth() + 1).padStart(2, '0');
  const dia = String(ahora.getDate()).padStart(2, '0');
  return `${ahora.getFullYear()}-${mes}-${dia}`;
}

/** El .SIN es texto plano ANSI/ASCII: se descarga byte a byte, sin pasar por UTF-8. */
function descargarSin(contenido: string, nombreArchivo: string) {
  const bytes = new Uint8Array(contenido.length);
  for (let i = 0; i < contenido.length; i++) bytes[i] = contenido.charCodeAt(i) & 0xff;
  const blob = new Blob([bytes as BlobPart], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombreArchivo;
  a.click();
  URL.revokeObjectURL(url);
}

export function GenerarArchivoBNDialog({ idPropuesta, cuentasUsadas, open, onClose }: Props) {
  const { data: cuentasBancoData } = useCuentasBancoBC();
  const cuentasBanco = cuentasBancoData?.data ?? [];
  const generar = useArchivoBancoBNPropuesta();

  const cuentasBN = useMemo(
    () =>
      cuentasUsadas.filter((n) => {
        const cuenta = cuentasBanco.find((c) => c.number === n);
        return cuenta ? esCuentaBNCR(cuenta.displayName) : false;
      }),
    [cuentasUsadas, cuentasBanco],
  );

  const [cuentaBancoOrigen, setCuentaBancoOrigen] = useState(cuentasBN[0] ?? '');
  const [transferenciaNo, setTransferenciaNo] = useState('');
  const [fecha, setFecha] = useState(hoyISO());
  const [resultado, setResultado] = useState<ArchivoBancoBNResultado | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: no depender de cuentaBancoOrigen, si no se resetea en cada cambio
  useEffect(() => {
    if (open && !cuentasBN.includes(cuentaBancoOrigen)) {
      setCuentaBancoOrigen(cuentasBN[0] ?? '');
    }
  }, [open, cuentasBN]);

  const trf = resultado?.trf ?? null;
  const sin = resultado?.sin ?? null;
  const Monto = resultado?.moneda === 'USD' ? MontoUSD : MontoCRC;
  const transferenciaValida =
    Number.isInteger(Number(transferenciaNo)) &&
    Number(transferenciaNo) >= 1 &&
    Number(transferenciaNo) <= 99999;

  function generarArchivo() {
    if (!cuentaBancoOrigen || !transferenciaValida) return;
    setResultado(null);
    generar.mutate(
      { id: idPropuesta, cuentaBancoOrigen, transferenciaNo: Number(transferenciaNo), fecha },
      {
        onSuccess: (r) => {
          if (r.data) setResultado(r.data);
          else toast.error(r.error?.mensaje ?? 'Error al generar el archivo');
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'Error al generar el archivo'),
      },
    );
  }

  function cerrar() {
    setResultado(null);
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generar archivo del Banco Nacional</DialogTitle>
          <DialogDescription>
            Arma el .TRF (pagos a cuentas del Banco Nacional, sin comisión) y el .SIN (otros bancos,
            por SINPE). Ábrelos en el Módulo Local del BN, formato IBAN: ahí se validan y se les
            agrega la TESTKEY antes de subirlos.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="cuenta-bn-origen">Cuenta bancaria de origen</Label>
            <select
              id="cuenta-bn-origen"
              value={cuentaBancoOrigen}
              onChange={(e) => {
                setCuentaBancoOrigen(e.target.value);
                setResultado(null);
              }}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              {cuentasBN.length === 0 && <option value="">Sin cuentas BN en la propuesta</option>}
              {cuentasBN.map((numero) => {
                const cuenta = cuentasBanco.find((c) => c.number === numero);
                return (
                  <option key={numero} value={numero}>
                    {cuenta ? `${cuenta.displayName} (${cuenta.number})` : numero}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="transferencia-bn">Número de transferencia</Label>
              <Input
                id="transferencia-bn"
                type="number"
                value={transferenciaNo}
                onChange={(e) => {
                  setTransferenciaNo(e.target.value);
                  setResultado(null);
                }}
                placeholder="Ej. 16"
                min={1}
                max={99999}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                Consecutivo propio de cada archivo que se sube al BN.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="fecha-bn">Fecha de aplicación</Label>
              <Input
                id="fecha-bn"
                type="date"
                value={fecha}
                onChange={(e) => {
                  setFecha(e.target.value);
                  setResultado(null);
                }}
              />
            </div>
          </div>
        </div>

        {resultado && (
          <div className="space-y-3 rounded-md border border-adelante-primary/30 bg-adelante-primary/5 p-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Monto</p>
                <p className="font-mono text-base font-semibold text-adelante-primary-dark">
                  <Monto monto={resultado.montoTotal} />
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Cantidad de créditos</p>
                <p className="font-mono text-base font-semibold text-adelante-primary-dark">
                  {resultado.totalIncluidas}
                </p>
              </div>
            </div>
            {resultado.totalExcluidas > 0 && (
              <div className="space-y-1 border-t border-adelante-primary/20 pt-2">
                <p className="flex items-center gap-2 text-destructive">
                  <Warning className="h-4 w-4 shrink-0" weight="bold" aria-hidden="true" />
                  {resultado.totalExcluidas} proveedor(es) quedaron fuera del archivo:
                </p>
                <ul className="list-inside list-disc text-xs text-muted-foreground">
                  {resultado.excluidas.map((e) => (
                    <li key={e.vendorCode}>
                      {e.vendorName ?? e.vendorCode} ({e.vendorCode}) — {TEXTO_MOTIVO[e.motivo]}
                    </li>
                  ))}
                </ul>
                <a
                  href="/flujo-semanal/proveedores"
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-medium text-adelante-primary-dark underline"
                >
                  Corregirlos en Proveedores (otra pestaña) y generar de nuevo
                </a>
              </div>
            )}
          </div>
        )}

        <div className="flex min-w-0 flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={cerrar}>
            Cerrar
          </Button>
          {trf && (
            <Button onClick={() => descargarSin(trf.contenido, trf.nombreArchivoSugerido)}>
              <DownloadSimple className="h-4 w-4" weight="bold" aria-hidden="true" />
              Descargar .TRF ({trf.totalCreditos})
            </Button>
          )}
          {sin && (
            <Button onClick={() => descargarSin(sin.contenido, sin.nombreArchivoSugerido)}>
              <DownloadSimple className="h-4 w-4" weight="bold" aria-hidden="true" />
              Descargar .SIN ({sin.totalCreditos})
            </Button>
          )}
          <Button
            variant={resultado ? 'outline' : 'default'}
            onClick={generarArchivo}
            disabled={!cuentaBancoOrigen || !transferenciaValida || generar.isPending}
          >
            {generar.isPending ? 'Generando...' : resultado ? 'Generar de nuevo' : 'Generar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
